/**
 * Web storage adapter para Supabase auth.
 *
 * Dois modos:
 * - localStorage (default): sessão persiste entre aba/restart. Cada subdomínio
 *   tem seu próprio storage — sem SSO cross-subdomain.
 * - cookie (quando `cookieDomain` fornecido): sessão em cookie compartilhado
 *   no domínio pai (ex: `.playprosport.com.br`), habilitando SSO entre
 *   `www`, `app`, `admin`. Requer HTTPS + mesmo domínio registrável.
 *
 * Em `localhost` o cookie é rejeitado silenciosamente pelo browser — o adapter
 * cai pra localStorage automaticamente.
 *
 * Implementa ADR-0001 (SSO web ↔ web).
 */
import type { AuthStorage } from "../types";

const IN_MEMORY_FALLBACK = new Map<string, string>();

function hasLocalStorage(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, domain: string): void {
  if (typeof document === "undefined") return;
  const maxAge = 60 * 60 * 24 * 30; // 30 dias
  document.cookie =
    `${name}=${encodeURIComponent(value)};` +
    `domain=${domain};path=/;max-age=${maxAge};` +
    `SameSite=Lax;Secure`;
}

function deleteCookie(name: string, domain: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=;domain=${domain};path=/;max-age=0`;
}

/**
 * Cria um storage adapter web. Se `cookieDomain` for fornecido, usa cookie
 * compartilhado (SSO cross-subdomain). Senão, usa localStorage por-origem.
 */
export function createWebStorage(opts: { cookieDomain?: string } = {}): AuthStorage {
  const useCookie = !!opts.cookieDomain;

  return {
    getItem: (key: string): string | null => {
      if (useCookie) {
        const cookieValue = getCookie(key);
        if (cookieValue !== null) return cookieValue;
        // fallback: se cookie não persistiu (ex: localhost), usa localStorage
      }
      if (hasLocalStorage()) return window.localStorage.getItem(key);
      return IN_MEMORY_FALLBACK.get(key) ?? null;
    },

    setItem: (key: string, value: string): void => {
      if (useCookie) {
        setCookie(key, value, opts.cookieDomain!);
        // Dupla escrita em localStorage para resiliência offline + fallback dev
      }
      if (hasLocalStorage()) {
        window.localStorage.setItem(key, value);
      } else {
        IN_MEMORY_FALLBACK.set(key, value);
      }
    },

    removeItem: (key: string): void => {
      if (useCookie) deleteCookie(key, opts.cookieDomain!);
      if (hasLocalStorage()) window.localStorage.removeItem(key);
      IN_MEMORY_FALLBACK.delete(key);
    },
  };
}

/** Adapter web default (localStorage, sem cookie). Útil quando não há SSO configurado. */
export const webStorage: AuthStorage = createWebStorage();
